import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { baseRoleOf } from "@/config/roleViews";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauth, object=auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("erp_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("erp_token", data.access_token);
      localStorage.setItem("erp_user", JSON.stringify(data.user));
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  // Used by the Google OAuth callback page: we already have a valid access
  // token (issued by our backend after verifying the Google identity), just
  // need to fetch the user profile and populate the session with it.
  const loginWithToken = async (token) => {
    localStorage.setItem("erp_token", token);
    try {
      const { data } = await api.get("/auth/me");
      localStorage.setItem("erp_user", JSON.stringify(data));
      setUser(data);
      return { ok: true };
    } catch (e) {
      localStorage.removeItem("erp_token");
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = () => {
    localStorage.removeItem("erp_token");
    localStorage.removeItem("erp_user");
    sessionStorage.removeItem("erp_view_as");
    sessionStorage.removeItem("erp_landed");
    // the next person to log in on this tab (or this same person again) must re-enter the Finance
    // PIN — it unlocks Sales/Purchases for one person's session, not for whoever's using the tab
    sessionStorage.removeItem("erp_finance_token");
    setViewAsState(null);
    setUser(false);
  };

  // "View as": an admin can preview the app as another role (display only —
  // the server still enforces the admin's real permissions). `user` is the
  // effective user every page already reads; `realUser` is the signed-in one.
  const [viewAs, setViewAsState] = useState(() => sessionStorage.getItem("erp_view_as") || null);
  const setViewAs = (role) => {
    if (role) sessionStorage.setItem("erp_view_as", role);
    else sessionStorage.removeItem("erp_view_as");
    setViewAsState(role || null);
  };
  const realUser = user;
  const effectiveUser =
    user && user.role === "admin" && viewAs && viewAs !== "admin" ? { ...user, role: viewAs } : user;
  const previewing = effectiveUser !== realUser;

  return (
    <AuthContext.Provider
      value={{ user: effectiveUser, realUser, previewing, setViewAs, loading, login, loginWithToken, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/** True for the admin (and an admin view acting as admin), whatever the page; other views never. */
export function isAdmin(user) {
  return !!user && baseRoleOf(user.role) === "admin";
}

export function hasRole(user, ...roles) {
  if (!user) return false;
  const base = baseRoleOf(user.role); // finer views act with their core role's permissions
  if (base === "admin") return true;
  return roles.includes(base);
}
